import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';

// Helper function to extract a cookie value by name
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [loading, setLoading] = useState(true);

    const [dashboardData, setDashboardData] = useState({
        totalClicks: 0,
        totalConv: 0,
        totalEarn: "0.00",
        totalUsers: 0,
        graph: [],
        recentActivity: []
    });

    // Fetch dashboard data from backend API
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const baseUrl = process.env.REACT_APP_API_URL || '';
                const token = getCookie('adminToken') || getCookie('authToken');

                const response = await axios.get(`${baseUrl}/api/admin/dashboard`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.data && response.data.status) {
                    const d = response.data.data || {};
                    setDashboardData({
                        totalClicks: d.totalClicks ?? 0,
                        totalConv: d.totalConv ?? 0,
                        totalEarn: d.totalEarn ?? "0.00",
                        totalUsers: d.totalUsers ?? 0,
                        graph: d.graph || [],
                        recentActivity: d.recentActivity || []
                    });
                }
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
                if (error.response?.data?.msg) {
                    toast.error(error.response.data.msg);
                } else {
                    toast.error("Failed to load dashboard data");
                }
                if (error.response && error.response.status === 401) {
                    navigate('/emAdmin7/login');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [navigate]);

    // Format dynamic backend graph data into SVG path coordinates
    const rawGraphData = dashboardData.graph || [];
    const totalPoints = rawGraphData.length;

    const graphData = rawGraphData.map((pt, idx) => ({
        day: pt.day || '',
        clicks: pt.clicks || 0,
        conv: pt.conv || 0,
        earnings: pt.earn || 0,
        x: totalPoints > 1 ? (idx / (totalPoints - 1)) * 100 : 0
    }));

    // Dynamic SVG Path Generators based on dataset points
    const getPath = (key) => {
        if (!graphData || graphData.length === 0) return '';
        const maxVal = Math.max(...graphData.map((d) => d[key]), 1);

        return graphData.reduce((acc, pt, i, arr) => {
            const y = 90 - (pt[key] / maxVal) * 80;
            if (i === 0) return `M ${pt.x},${y}`;

            const prevPt = arr[i - 1];
            const prevY = 90 - (prevPt[key] / maxVal) * 80;
            const cp1X = prevPt.x + (pt.x - prevPt.x) / 2;
            const cp2X = cp1X;

            return `${acc} C ${cp1X},${prevY} ${cp2X},${y} ${pt.x},${y}`;
        }, '');
    };

    const quickStats = [
        {
            title: 'Total Clicks',
            value: loading ? '...' : (dashboardData.totalClicks || 0).toLocaleString(),
            color: 'purple',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" />
                </svg>
            )
        },
        {
            title: 'Conversions',
            value: loading ? '...' : (dashboardData.totalConv || 0).toLocaleString(),
            color: 'amber',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                    <path d="M16 16h5v5" />
                </svg>
            )
        },
        {
            title: 'Net Earnings',
            value: loading ? '...' : `₹${dashboardData.totalEarn || "0.00"}`,
            color: 'emerald',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
                    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
                </svg>
            )
        },
        {
            title: 'Total Users',
            value: loading ? '...' : (dashboardData.totalUsers || 0).toLocaleString(),
            color: 'blue',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
            )
        }
    ];

    const getColorClasses = (color) => {
        switch (color) {
            case 'purple':
                return { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20', glow: 'bg-purple-500', hoverBorder: 'hover:border-purple-500/50 hover:shadow-purple-500/10' };
            case 'amber':
                return { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', glow: 'bg-amber-500', hoverBorder: 'hover:border-amber-500/50 hover:shadow-amber-500/10' };
            case 'emerald':
                return { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', glow: 'bg-emerald-500', hoverBorder: 'hover:border-emerald-500/50 hover:shadow-emerald-500/10' };
            case 'blue':
                return { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', glow: 'bg-blue-500', hoverBorder: 'hover:border-blue-500/50 hover:shadow-blue-500/10' };
            default:
                return { text: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', glow: 'bg-zinc-500', hoverBorder: 'hover:border-zinc-500/50' };
        }
    };

    return (
        <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
            <style>{`
                @keyframes drawWave {
                    to { stroke-dashoffset: 0; }
                }
                .animate-draw {
                    stroke-dasharray: 1000;
                    stroke-dashoffset: 1000;
                    animation: drawWave 1.8s ease-out forwards;
                }

                @keyframes springIn {
                    0% { opacity: 0; transform: translateY(20px) scale(0.97); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                .animate-spring-in {
                    animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                .shimmer-bg {
                    background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.8s infinite;
                }

                .micro-interaction {
                    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .micro-interaction:hover {
                    transform: translateY(-2px);
                }
                .micro-interaction:active {
                    transform: translateY(0px) scale(0.98);
                }
            `}</style>

            <div className="p-4 sm:p-8 space-y-8 max-w-[1600px] mx-auto mt-2">

                {/* ========== TOP METRIC CARDS ========== */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {quickStats.map((stat, idx) => {
                        const c = getColorClasses(stat.color);
                        return (
                            <div
                                key={idx}
                                className={`group relative bg-zinc-900/90 p-6 rounded-3xl border border-zinc-800 shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-default ${c.hoverBorder} animate-spring-in`}
                                style={{ animationDelay: `${idx * 60}ms` }}
                            >
                                <div
                                    className={`absolute top-0 right-0 w-28 h-28 -mr-8 -mt-8 rounded-full blur-2xl transition-all duration-500 opacity-20 group-hover:opacity-40 group-hover:scale-125 ${c.glow}`}
                                />

                                <div className="flex items-center justify-between mb-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${c.bg} ${c.text} border ${c.border}`}>
                                        {stat.icon}
                                    </div>
                                </div>

                                <p className="text-zinc-400 text-sm font-medium">{stat.title}</p>
                                {loading ? (
                                    <div className="mt-2 w-24 h-8 bg-zinc-800/60 rounded-lg shimmer-bg" />
                                ) : (
                                    <h3 className="text-3xl font-extrabold mt-1 tracking-tight text-zinc-100">
                                        {stat.value}
                                    </h3>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ========== CUSTOM SVG GRAPH SECTION ========== */}
                <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-lg relative overflow-hidden animate-spring-in">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                        <div>
                            <h3 className="text-xl font-bold text-zinc-100">Weekly Performance</h3>
                            <p className="text-sm text-zinc-400 mt-0.5">Smooth wave analysis of your traffic</p>
                        </div>

                        <div className="flex items-center gap-6">
                            <div className="hidden md:flex flex-wrap gap-4 text-[11px] font-bold uppercase tracking-wider">
                                <div className="flex items-center gap-1.5 text-purple-400">
                                    <span className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse shadow-[0_0_8px_#c084fc]" /> Clicks
                                </div>
                                <div className="flex items-center gap-1.5 text-amber-400">
                                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Conv
                                </div>
                                <div className="flex items-center gap-1.5 text-emerald-400">
                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Earnings
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative h-72 w-full px-2">
                        {/* Loading Skeleton for graph */}
                        {loading && (
                            <div className="absolute inset-0 flex items-end justify-between gap-2 px-4 pb-4">
                                {[...Array(7)].map((_, i) => (
                                    <div key={i} className="flex-1 bg-zinc-800/40 rounded-t-lg shimmer-bg" style={{ height: `${20 + Math.random() * 60}%` }} />
                                ))}
                            </div>
                        )}

                        {/* Interactive Tooltip */}
                        {!loading && hoveredPoint && (
                            <div
                                className="absolute z-30 pointer-events-none transition-all duration-150 ease-out -translate-x-1/2 -translate-y-full mb-3"
                                style={{
                                    left: `${hoveredPoint.x}%`,
                                    top: '25%'
                                }}
                            >
                                <div className="bg-zinc-950/95 backdrop-blur-md text-zinc-100 text-xs rounded-2xl p-3.5 shadow-2xl border border-zinc-800 space-y-1.5 min-w-[130px]">
                                    <p className="font-bold text-purple-300 pb-1 border-b border-zinc-800">
                                        {hoveredPoint.day} Performance
                                    </p>
                                    <div className="flex items-center justify-between text-purple-400 font-semibold">
                                        <span>Clicks:</span> <span>{hoveredPoint.clicks}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-amber-400 font-semibold">
                                        <span>Conversions:</span> <span>{hoveredPoint.conv}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-emerald-400 font-semibold">
                                        <span>Earnings:</span> <span>₹{hoveredPoint.earnings}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!loading && (
                            <svg className="w-full h-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 100 100">
                                {/* Grid Lines */}
                                {graphData.map((d, i) => (
                                    <line
                                        key={i}
                                        x1={d.x}
                                        y1="0"
                                        x2={d.x}
                                        y2="100"
                                        stroke="#27272a"
                                        strokeWidth="0.5"
                                        strokeDasharray="4"
                                    />
                                ))}

                                {/* Earnings Path (Emerald Green) */}
                                <path
                                    d={getPath('earnings')}
                                    fill="none"
                                    stroke="#10b981"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    className="animate-draw drop-shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                                />

                                {/* Conversions Path (Amber/Orange) */}
                                <path
                                    d={getPath('conv')}
                                    fill="none"
                                    stroke="#f59e0b"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    className="animate-draw drop-shadow-[0_0_10px_rgba(245,158,11,0.3)]"
                                />

                                {/* Clicks Path (Neon Purple) */}
                                <path
                                    d={getPath('clicks')}
                                    fill="none"
                                    stroke="#a855f7"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    className="animate-draw drop-shadow-[0_0_12px_rgba(168,85,247,0.6)]"
                                />

                                {/* Hover Interaction Hotspots */}
                                <g>
                                    {graphData.map((pt, idx) => (
                                        <rect
                                            key={idx}
                                            x={pt.x - 5}
                                            y="0"
                                            width="10"
                                            height="100"
                                            fill="transparent"
                                            className="cursor-pointer"
                                            onMouseEnter={() => setHoveredPoint(pt)}
                                            onMouseLeave={() => setHoveredPoint(null)}
                                        />
                                    ))}
                                </g>
                            </svg>
                        )}
                    </div>

                    <div className="flex justify-between mt-6 px-1">
                        {graphData.map((d, i) => (
                            <span
                                key={i}
                                className={`text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${hoveredPoint?.day === d.day ? 'text-purple-400 font-black' : 'text-zinc-500'}`}
                            >
                                {d.day}
                            </span>
                        ))}
                    </div>
                </div>

                {/* ========== RECENT ACTIVITY (Limited to 5) ========== */}
                <div className="space-y-6 animate-spring-in">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 bg-purple-500/10 text-purple-400 rounded-xl border border-purple-500/20">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-zinc-100">Recent Activity</h3>
                        </div>
                        <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest">
                            Latest 5 events
                        </span>
                    </div>

                    <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl overflow-hidden shadow-lg">
                        {loading ? (
                            <div className="p-4 space-y-3">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-950/40">
                                        <div className="w-10 h-10 rounded-xl bg-zinc-800 shimmer-bg" />
                                        <div className="flex-1 space-y-2">
                                            <div className="w-2/3 h-3 bg-zinc-800 rounded shimmer-bg" />
                                            <div className="w-1/3 h-2 bg-zinc-800 rounded shimmer-bg" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : dashboardData.recentActivity.length === 0 ? (
                            <div className="p-10 text-center">
                                <p className="text-sm text-zinc-500 font-medium italic">No recent activity yet.</p>
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-zinc-800/60">
                                    {dashboardData.recentActivity.slice(0, 5).map((act, idx) => {
                                        const isConv = act.type === 'conv';
                                        return (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-4 p-4 sm:px-6 hover:bg-zinc-950/40 transition-colors duration-200 group"
                                            >
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-transform duration-300 group-hover:scale-110 ${isConv
                                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                                    : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                                                    }`}>
                                                    {isConv ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" />
                                                        </svg>
                                                    )}
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-zinc-200 truncate group-hover:text-white transition-colors duration-200">
                                                        {act.title}
                                                    </p>
                                                    <p className="text-[11px] font-medium text-zinc-500 mt-0.5">
                                                        {act.time}
                                                    </p>
                                                </div>

                                                <div className="shrink-0">
                                                    {isConv && act.amount > 0 ? (
                                                        <span className="text-xs font-black text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-lg">
                                                            +₹{act.amount}
                                                        </span>
                                                    ) : (
                                                        <span className={`text-xs font-black px-3 py-1 rounded-lg ${isConv
                                                            ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                                                            : 'text-purple-400 bg-purple-500/10 border border-purple-500/20'
                                                            }`}>
                                                            {isConv ? 'CONV' : 'CLICK'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ========== VIEW ALL LOGS BUTTON ========== */}
                                <div className="p-4 sm:p-6 bg-zinc-950/40 border-t border-zinc-800/60">
                                    <button
                                        onClick={() => navigate('/emAdmin7/logs')}
                                        className="w-full py-3.5 bg-zinc-900 border border-zinc-800 hover:border-purple-500/50 hover:bg-purple-500/5 text-zinc-200 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all micro-interaction group"
                                    >
                                        <span>View All Logs</span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="transition-transform duration-300 group-hover:translate-x-1"
                                        >
                                            <path d="M5 12h14" />
                                            <path d="m12 5 7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminDashboard;